<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50 pb-20">
      <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Academic Resources</h1>
            <p class="text-blue-100"> Access UP Yearbooks, important documents, and guides for your academic journey</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Search Filter -->
      <div class="mb-8 max-w-2xl mx-auto">
        <div class="bg-white border border-gray-100 rounded-lg p-3 shadow-sm space-y-3">
          <div class="flex items-center gap-2">
            <div class="relative flex-1 min-w-0">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                <span class="material-symbols-outlined">search</span>
              </span>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search yearbooks, resources, and documents..."
                class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-300 text-left"
              />
            </div>
            <button
              type="button"
              class="h-12 w-12 rounded-lg border inline-flex items-center justify-center transition-colors relative shrink-0"
              :class="activeFilterCount ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-blue-50 hover:text-blue-700'"
              aria-label="Open filters"
              @click="showFilterSheet = true"
            >
              <span class="material-symbols-outlined">tune</span>
              <span
                v-if="activeFilterCount"
                class="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center"
              >
                {{ activeFilterCount }}
              </span>
            </button>
          </div>
          <div v-if="activeFilterCount" class="flex flex-wrap items-center gap-2 text-xs">
            <span class="px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              {{ activeFilterCount }} filter{{ activeFilterCount === 1 ? '' : 's' }} active
            </span>
          </div>
        </div>
      </div>

      <!-- Faculty Yearbooks Section -->
      <div v-if="!filters.section || filters.section === 'yearbooks'" id="yearbooks" class="mb-10 scroll-mt-4">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-2xl font-bold text-blue-900 flex items-center">
            <span class="material-symbols-outlined mr-2 text-yellow-500">auto_stories</span>
            UP Faculty Yearbooks {{ currentYear }}
          </h2>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <YearbookCard 
            v-for="yearbook in filteredYearbooks"
            :key="yearbook.code"
            :yearbook="yearbook"
            :isUserFaculty="isUserFaculty(yearbook)"
          />
        </div>

        <!-- Empty state for yearbook search -->
        <div v-if="filteredYearbooks.length === 0 && (searchQuery || activeFilterCount)" class="text-center py-12">
          <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">search_off</span>
          <h3 class="text-lg font-medium text-gray-700 mb-2">No yearbooks found</h3>
          <p class="text-gray-500">Try searching with different keywords or adjusting your filters</p>
        </div>
      </div>

      <!-- Student Services & Support Section -->
      <div v-if="!filters.section || filters.section === 'services'" id="services" class="mb-10 scroll-mt-4">
        <h2 class="text-2xl font-bold text-blue-900 mb-6 flex items-center">
          <span class="material-symbols-outlined mr-2 text-green-600">support_agent</span>
          Student Services & Support
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ContactCard
            v-for="(service, index) in filteredStudentServices"
            :key="'service-' + index"
            :service="service"
          />
        </div>

        <!-- Empty state for services search -->
        <div v-if="filteredStudentServices.length === 0 && searchQuery" class="text-center py-12">
          <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">search_off</span>
          <h3 class="text-lg font-medium text-gray-700 mb-2">No services found</h3>
          <p class="text-gray-500">Try searching with different keywords</p>
        </div>
      </div>

      <!-- Campus Locations Section -->
      <div v-if="!filters.section || filters.section === 'locations'" id="locations" class="mb-8 scroll-mt-4">
        <h2 class="text-2xl font-bold text-blue-900 mb-6 flex items-center">
          <span class="material-symbols-outlined mr-2 text-purple-600">location_on</span>
          Campus Locations
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CampusMapCard
            v-for="(location, index) in filteredCampusLocations"
            :key="'location-' + index"
            :campus="location"
          />
        </div>

        <!-- Empty state for locations search -->
        <div v-if="filteredCampusLocations.length === 0 && searchQuery" class="text-center py-12">
          <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">search_off</span>
          <h3 class="text-lg font-medium text-gray-700 mb-2">No locations found</h3>
          <p class="text-gray-500 mb-4">
            We couldn't find any locations matching "{{ searchQuery }}"
          </p>
          <button 
            @click="searchQuery = ''" 
            class="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition-colors"
          >
            Clear search
          </button>
        </div>
      </div>
    </div>
  </div>

  <resources-filter-sheet
    v-model="showFilterSheet"
    :filters="filters"
    :faculties="faculties"
    @apply="applyFilters"
  />

  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import ResourcesFilterSheet from '@/components/resources/ResourcesFilterSheet.vue';
import YearbookCard from '@/components/resources/YearbookCard.vue';
import ContactCard from '@/components/resources/ContactCard.vue';
import CampusMapCard from '@/components/resources/CampusMapCard.vue';
import { useResources } from '@/composables/useResources';
import { mapState } from 'vuex';

export default {
  name: "ResourcesPage",
  components: {
    AppHeader,
    BottomNav,
    ResourcesFilterSheet,
    YearbookCard,
    ContactCard,
    CampusMapCard,
  },
  data() {
    return {
      searchQuery: '',
      showFilterSheet: false,
      filters: {
        section: '',
        faculty: ''
      }
    };
  },
  setup() {
    const {
      currentYear,
      getYearbooks,
      sortYearbooksByUserFaculty,
      getStudentServices,
      getCampusLocations,
      faculties
    } = useResources();

    return {
      currentYear,
      getYearbooks,
      sortYearbooksByUserFaculty,
      getStudentServices,
      getCampusLocations,
      faculties
    };
  },
  computed: {
    ...mapState(['profile', 'user']),

    activeFilterCount() {
      return Object.values(this.filters).filter(Boolean).length;
    },

    // Get yearbooks sorted by user's faculty
    yearbooks() {
      const yearbooks = this.getYearbooks(this.currentYear);
      const userFaculty = this.profile?.university?.faculty;
      return this.sortYearbooksByUserFaculty(yearbooks, userFaculty);
    },

    // Filter yearbooks based on search and the faculty filter
    filteredYearbooks() {
      let results = this.yearbooks;

      if (this.filters.faculty) {
        results = results.filter(yearbook => yearbook.code === this.filters.faculty);
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        results = results.filter(yearbook =>
          yearbook.name.toLowerCase().includes(query) ||
          yearbook.code.toLowerCase().includes(query) ||
          yearbook.description.toLowerCase().includes(query)
        );
      }

      return results;
    },

    // Get student services
    studentServices() {
      return this.getStudentServices();
    },

    // Filter student services based on search
    filteredStudentServices() {
      if (!this.searchQuery) return this.studentServices;
      
      const query = this.searchQuery.toLowerCase();
      return this.studentServices.filter(service =>
        (service.title && service.title.toLowerCase().includes(query)) ||
        (service.description && service.description.toLowerCase().includes(query)) ||
        (service.contacts && service.contacts.some(contact => 
          (contact.label && contact.label.toLowerCase().includes(query)) ||
          (contact.value && contact.value.toLowerCase().includes(query))
        ))
      );
    },

    // Get campus locations
    campusLocations() {
      return this.getCampusLocations();
    },

    // Filter campus locations based on search
    filteredCampusLocations() {
      if (!this.searchQuery) return this.campusLocations;
      
      const query = this.searchQuery.toLowerCase();
      return this.campusLocations.filter(location =>
        location.name.toLowerCase().includes(query) ||
        location.description.toLowerCase().includes(query)
      );
    }
  },
  methods: {
    // Check if yearbook matches user's faculty
    isUserFaculty(yearbook) {
      if (!this.profile?.university?.faculty) return false;
      
      const userFaculty = this.profile.university.faculty.toLowerCase();
      return yearbook.name.toLowerCase().includes(userFaculty) ||
             yearbook.code.toLowerCase() === userFaculty;
    },

    applyFilters(newFilters) {
      this.filters = newFilters;
    }
  }
};
</script>

<style scoped>
/* Smooth scroll offset for fixed header */
.scroll-mt-4 {
  scroll-margin-top: 1rem;
}

/* Additional page-specific styles if needed */
.material-symbols-outlined {
  font-variation-settings:
  'FILL' 0,
  'wght' 400,
  'GRAD' 0,
  'opsz' 24;
}
</style>

